import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { AppStateService } from '../../app-state.service';
import { FormConfigService } from '../forms-config.service';
import { PatientProfile } from '../patient.model';
import { AddressAutoCompleteComponent } from '../address-auto-complete/address-auto-complete.component';
import { AutoSlashDateFormatDirective } from '../../auto-slash-date-format.directive';
import { AutoDashSSNFormatDirective } from '../../auto-ssn-format.directive';

@Component({
  selector: 'app-profile-manage',
  standalone: true,
  templateUrl: './profile-manage.component.html',
  styleUrls: ['./profile-manage.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCardModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    MatNativeDateModule,
    AddressAutoCompleteComponent,
    AutoDashSSNFormatDirective,
    MatIconModule
]
})
export class ProfileManageComponent implements OnInit {
  profileForm: FormGroup;
  loading = true;
  clientId: string;
  patientId: string;
  isFormEdited = false;
  hide = true;

  maritalStatuses = ['Single', 'Married', 'Child', 'Widowed', 'Divorced'];
  genders = ['Male', 'Female', 'Unknown'];
  contactMethods = ['None', 'DoNotCall', 'HmPhone', 'WkPhone', 'WirelessPh', 'Email', 'Mail'];


  states: string[] = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia',
    'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland',
    'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
    'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
    'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
    'West Virginia', 'Wisconsin', 'Wyoming'
  ];

  constructor(
    private fb: FormBuilder,
    private appStateService: AppStateService,
    private formConfigService: FormConfigService,
    private snackBar: MatSnackBar,
    private router: Router
  ) {
    this.clientId = this.appStateService.getState().clientId || '';
    this.patientId = this.appStateService.getState().patientId || '';

    this.profileForm = this.fb.group({
      patientNumber: [{ value: '', disabled: true }],
      lastName: ['', Validators.required],
      firstName: ['', Validators.required],
      gender: ['Unknown'],
      maritalStatus: ['Single'],
      dateOfBirth: [new Date(), Validators.required],
      phoneNumber: ['', [Validators.required, this.phoneNumberValidator()]],
      email: ['', [Validators.required, Validators.email]],
      patientStatus: [{ value: 'Patient', disabled: true }],
      textMessageConsent: ['Yes'],
      isNewPatient: [{ value: 'No', disabled: true }],
      fullAddress: [''],
      address: ['', Validators.required],
      city: ['', Validators.required],
      state: ['', Validators.required],
      zipCode: ['', [Validators.required, this.zipCodeValidator()]],
      ssn: [''],
      language: [{ value: '', disabled: true }],
      preferredContactMethod: [''],
      id: [{ value: '', disabled: true }],
      isRegistered: [{ value: '', disabled: true }]
    });

    this.profileForm.valueChanges.subscribe(() => {
      this.isFormEdited = true;
    });
  }

  phoneNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const phoneNumberPattern = /^\+?1?\d{10,12}$/; // Adjust the pattern as necessary
      const valid = phoneNumberPattern.test(control.value);
      return valid ? null : { invalidPhoneNumber: { value: control.value } };
    };
  }

  zipCodeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const zipCodePattern = /^[0-9]{5}(?:-[0-9]{4})?$/;
      const valid = zipCodePattern.test(control.value);
      return valid ? null : { invalidZipCode: { value: control.value } };
    };
  }

  ngOnInit(): void {
    this.loadPatientProfile();
  }


  loadPatientProfile(): void {
    this.formConfigService.fetchPatient(this.clientId, this.patientId).subscribe(
      (patientData: PatientProfile) => {
        let patientDataCopy: any = { ...patientData };
        if (patientData.dateOfBirth) {
          patientDataCopy.dateOfBirth = this.convertToDate(patientData.dateOfBirth);
        }
        this.profileForm.patchValue(patientDataCopy);
        this.loading = false;
        this.isFormEdited = false;
      },
      error => {
        this.showError('Error loading patient profile.');
        this.loading = false;
      }
    );
  }

  adjustDateForTimezone(date: Date): Date {
    date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
    return date;
  }

  convertToDate(dateString: string): Date | null {
    if (!dateString) {
      return null;
    }

    let parsedDate = new Date(dateString);
    if (!isNaN(parsedDate.getTime())) {
      return this.adjustDateForTimezone(parsedDate);
    }

    let dateOnlyString = dateString.split('T')[0].split(' ')[0];
    const [year, month, day] = dateOnlyString.split('-');
    if (year && month && day) {
      return this.adjustDateForTimezone(new Date(Number(year), Number(month) - 1, Number(day)));
    }
    // If all else fails, return null indicating invalid date format
    return null;
  }

  toggleHide() {
    this.hide = !this.hide;
  }

  onSave(): void {
    if (this.profileForm.valid && this.isFormEdited) {
      const updatedProfile: any = this.profileForm.getRawValue();
      let patientDataCopy: any = { ...updatedProfile };
      // Convert dateOfBirth to string in YYYY-MM-DD format using local timezone
      if (updatedProfile.dateOfBirth instanceof Date) {
        const date = updatedProfile.dateOfBirth;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        patientDataCopy.dateOfBirth = `${year}-${month}-${day}`;
      }

      this.formConfigService.updatePatientData(this.clientId, this.patientId, patientDataCopy).subscribe(
        () => {
          this.snackBar.open('Profile updated successfully', 'Close', {
            duration: 3000,
          });
          this.router.navigate(['/dashboard', 'main']);
        },
        error => {
          this.snackBar.open('Failed to update profile', 'Close', {
            duration: 5000,
          });
        }
      );
    } else if (!this.isFormEdited) {
      this.snackBar.open('No changes detected in the form', 'Close', {
        duration: 3000,
      });
    } else {
      this.snackBar.open('Please correct the errors in the form', 'Close', {
        duration: 5000,
      });
    }
  }

  onCancel(): void {
    this.router.navigate(['/dashboard', 'main']);
  }

  private showError(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
    });
  }

  onAddressSelected(address: { street: string; city: string; state: string; stateCode: string; zip: string }): void {
    // Strip ZIP+4 to just 5 digits for consistency
    const zipCode = address.zip.includes('-') ? address.zip.split('-')[0] : address.zip;
    
    const updateData = {
      address: address.street,
      city: address.city,
      state: address.state, // Frontend dropdown expects full state names
      zipCode: zipCode
    };
    
    this.profileForm.patchValue(updateData);
    this.isFormEdited = true;
  }

}
